/**
 * heavyPolylineConverter — the legacy heavy `POLYLINE` (DXF `POLYLINE` +
 * `VERTEX` records) → CadPrimitive paths, for both the top-level plot
 * dispatcher and the INSERT walk (@codex PR #785 round 2, F1).
 *
 * A heavy POLYLINE is NOT an `LWPOLYLINE` with extra fields — it is four
 * different AutoCAD objects sharing one DXF entity, discriminated by group 70
 * (`polylineSubtype`, the one decode `drawing-model` owns):
 *
 *   - **2D polyline** — planar, authored in the entity's OCS at the ENTITY's
 *     elevation, with real per-vertex bulge arcs;
 *   - **3D polyline** — per-vertex WCS Z, and NO bulges at all (group 42 is a
 *     2D-polyline field, Autodesk DXF reference);
 *   - **polygon mesh** — an M×N grid whose wireframe is its rows and columns;
 *   - **poly-face mesh** — a vertex POOL plus FACE records whose 71..74
 *     indices name the visible edges.
 *
 * # Reused truths, NOT re-derived here
 *
 *   - the DRAWABLE topology of every subtype — which records are visible
 *     geometry (a SPLINE-fit polyline's control FRAME is NOT), in what order,
 *     which spans are curved, and the mesh rows / columns / face edges with
 *     their FACE-record filtering, hidden-edge and closed-in-M/N rules — is
 *     `@dxf-ai-editor/cad-core`'s {@link heavyPolylineRuns} — the SAME runs
 *     the render adapter and the picker consume, carrying INDICES so each
 *     consumer maps them through its own point resolver;
 *   - the bulge → arc decode is the shared `bulgeToArc` every other converter
 *     uses, mapped through the affine by {@link appendTransformedBulgeSpan};
 *   - the OCS → WCS lift is `./ocs`'s Arbitrary Axis Algorithm map.
 *
 * # The 2D elevation (round-1 F4, restated here because the plot re-hits it)
 *
 * A 2D heavy polyline's plane is the ENTITY's `elevation` (the POLYLINE's own
 * 10/20/30 dummy point, whose Z is the elevation). A VERTEX's own `location.z`
 * is an OCS duplicate producers routinely write as 0 — and the schema's
 * `drawingPoint3DSchema` `.default(0)` ALWAYS materialises it, so a
 * `location.z ?? elevation` fallback is unreachable. On a planar plot the
 * elevation is invisible for the DEFAULT extrusion, but under a tilted OCS
 * normal it moves the polyline across the page (`P_wcs = x·Ax + y·Ay + z·N`),
 * so taking it from the wrong field mis-places the whole entity. Sourced by
 * SUBTYPE, exactly like `polylineFromEntity.ts` (render) and
 * `wcsBoundsLeaf.ts::heavyPolylineLocal` (bounds).
 *
 * # Plotted / NOT plotted (honest boundary)
 *
 * PLOTTED: the stroked centreline of every subtype's wire runs, bulge arcs as
 * exact cubics, the closed / closed-in-N wraps, hidden poly-face edges omitted,
 * the OCS lift, and the full INSERT affine when the polyline is a block child.
 *
 * PLOTTED, from the SAME geometry the screen draws: per-vertex / entity-default
 * WIDTH bands (groups 40/41). A width-carrying 2D / 3D vertex run plots as the
 * FILLED band whose silhouette is `widePolylineOutlineLoops`
 * (`scene3d/adapters/geom/polylineWidthOutline.ts`) — the identical call
 * `batchedLayer.ts` makes — routed through `./widePolylineBand.ts`, which owns
 * the FILLMODE decision and the nonzero fill rule. A mesh subtype's wire runs
 * carry no width and stay stroked. (This is what round 3 recorded as diverging
 * from the screen; it no longer does.)
 *
 * NOT PLOTTED (each matching what the editor itself draws, so the plot cannot
 * disagree with the screen):
 *   - group 75 SMOOTH-SURFACE tessellation (quadratic / cubic B-spline /
 *     Bezier surfaces at the 73/74 densities) — the control mesh is drawn
 *     instead, which is what the renderer draws (SPLFRAME-on parity);
 *   - group 39 THICKNESS extrusion — no converter in this exporter plots
 *     thickness for any entity.
 */

import {
  polylineSubtype,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";
import { heavyPolylineRuns, type HeavyPolylineRun } from "@dxf-ai-editor/cad-core";

import type {
  CadConvertContext,
  CadPrimitive,
  PathSegment,
  Point2,
} from "../cadPrimitive";
import { apply, compose, IDENTITY, type Affine2D } from "./affine2d";
import { isDefaultOcs, ocsToWcs2d } from "./ocs";
import { strokedPathPrimitives } from "./strokedPathPrimitive";
import { appendTransformedBulgeSpan } from "./transformedArc";
import {
  bandPrimitives,
  heavyPolylineBand,
  type WidePolylineBand,
} from "./widePolylineBand";

/** One emitted subpath of a heavy polyline (a mesh yields several). */
export interface HeavyPolylinePath {
  start: Point2;
  segments: PathSegment[];
  closed: boolean;
}

/**
 * The entity's OCS → WCS-XY lift as an {@link Affine2D}.
 *
 * Only a 2D polyline has an OCS: a 3D polyline / polygon mesh / poly-face mesh
 * stores WCS vertices, so lifting them would MOVE the geometry (the same rule
 * `heavyPolylineLocal` applies when it reports `extrusion: undefined` for those
 * subtypes). The linear columns are the WCS-XY images of the OCS X / Y axes and
 * the translation is the image of the elevation along the normal —
 * `P_wcs = x·Ax + y·Ay + elevation·N`, projected by dropping world Z (`./ocs`).
 * Default (0,0,1) normal ⇒ IDENTITY, so the common case adds no math.
 */
function ocsMatrix(entity: PolylineEntity): Affine2D {
  if (polylineSubtype(entity) !== "2d") return IDENTITY;
  if (isDefaultOcs(entity.extrusion)) return IDENTITY;
  const lift = ocsToWcs2d(entity.extrusion);
  const elevation = entity.elevation.z ?? 0;
  const ex = lift(1, 0);
  const ey = lift(0, 1);
  const origin = lift(0, 0, elevation);
  return { a: ex.x, b: ex.y, c: ey.x, d: ey.y, e: origin.x, f: origin.y };
}

/** A vertex's OCS/WCS XY (the Z never reaches a planar plot except via the lift). */
function vertexXY(entity: PolylineEntity, index: number): Point2 {
  const v = entity.vertices[index]!;
  return { x: v.location.x, y: v.location.y };
}

/**
 * The 2D / 3D vertex run as one path: the run's VISIBLE records in order, with
 * its per-span bulges (already pinned to 0 for a 3D polyline by the shared
 * topology, since group 42 is a 2D-polyline field).
 */
function vertexRunPath(
  entity: PolylineEntity,
  matrix: Affine2D,
  run: HeavyPolylineRun | undefined,
): HeavyPolylinePath[] {
  const idx = liveRunIndices(entity, run);
  if (idx.length < 2) return [];
  const closed = run!.closed;
  const bulges = run!.bulges;
  const segments: PathSegment[] = [];
  for (let k = 0; k < idx.length - 1; k += 1) {
    appendTransformedBulgeSpan(
      segments,
      vertexXY(entity, idx[k]!),
      vertexXY(entity, idx[k + 1]!),
      bulges[k] ?? 0,
      matrix,
    );
  }
  if (closed) {
    appendTransformedBulgeSpan(
      segments,
      vertexXY(entity, idx[idx.length - 1]!),
      vertexXY(entity, idx[0]!),
      bulges[idx.length - 1] ?? 0,
      matrix,
    );
  }
  return [
    {
      start: applyToVertex(entity, idx[0]!, matrix),
      segments,
      closed,
    },
  ];
}

/**
 * The run's indices that actually resolve to a stored record. A run carries
 * INDICES (never coordinates), and a malformed file can name one that is not
 * there; dropping it here keeps the centreline path and the width band walking
 * the SAME record list, so the band can never be built over a different run
 * than the one that would be stroked.
 */
function liveRunIndices(
  entity: PolylineEntity,
  run: HeavyPolylineRun | undefined,
): number[] {
  return (run?.indices ?? []).filter((i) => entity.vertices[i] !== undefined);
}

/** A vertex mapped through the composed transform. */
function applyToVertex(
  entity: PolylineEntity,
  index: number,
  matrix: Affine2D,
): Point2 {
  return apply(matrix, vertexXY(entity, index));
}

/**
 * The mesh wire runs as paths. A run carries INDICES into `entity.vertices`
 * (never coordinates — {@link heavyPolylineRuns}'s point-type-agnostic
 * contract), so the mapping to plot points happens here and a mesh vertex is
 * never confused with a FACE record's filler location.
 */
function meshPaths(
  entity: PolylineEntity,
  matrix: Affine2D,
  runs: readonly HeavyPolylineRun[],
): HeavyPolylinePath[] {
  const out: HeavyPolylinePath[] = [];
  for (const run of runs) {
    const idx = run.indices.filter((i) => entity.vertices[i] !== undefined);
    if (idx.length < 2) continue;
    const segments: PathSegment[] = [];
    for (let i = 1; i < idx.length; i += 1) {
      segments.push({
        kind: "line",
        to: applyToVertex(entity, idx[i]!, matrix),
      });
    }
    // A closed run wraps back to its first vertex — the same wrap the render
    // adapter's `closed` polyline geometry draws for a closed-in-M/N mesh.
    if (run.closed) {
      segments.push({
        kind: "line",
        to: applyToVertex(entity, idx[0]!, matrix),
      });
    }
    out.push({
      start: applyToVertex(entity, idx[0]!, matrix),
      segments,
      closed: run.closed,
    });
  }
  return out;
}

/**
 * The heavy polyline's plot paths under `outer` (IDENTITY at top level, the
 * composed block affine for a block child). The entity's own OCS lift is
 * composed INSIDE `outer`, so a block-nested OCS-authored polyline rides both.
 */
export function heavyPolylinePaths(
  entity: PolylineEntity,
  outer: Affine2D = IDENTITY,
): HeavyPolylinePath[] {
  const matrix = compose(outer, ocsMatrix(entity));
  const subtype = polylineSubtype(entity);
  const runs = heavyPolylineRuns(entity);
  if (subtype === "polygon-mesh" || subtype === "polyface-mesh") {
    return meshPaths(entity, matrix, runs);
  }
  return vertexRunPath(entity, matrix, runs[0]);
}

/**
 * The heavy polyline's WIDTH BAND under `outer`, or `null` when it declares no
 * width (groups 40/41 per record, or the POLYLINE record's own defaults) and
 * therefore plots as the stroked centreline {@link heavyPolylinePaths} builds.
 *
 * Exported so `insertConverter.ts`'s block-child arm plots the identical band
 * for a nested polyline — the same reason {@link heavyPolylinePaths} is
 * exported, and the same composition of the entity's OCS lift inside `outer`.
 * A mesh subtype has no band: its wire runs are a wireframe, which is why the
 * renderer builds them as `mesh-wire` geometry with no `widths` field.
 */
export function heavyPolylineBandPath(
  entity: PolylineEntity,
  outer: Affine2D = IDENTITY,
): WidePolylineBand | null {
  const subtype = polylineSubtype(entity);
  if (subtype === "polygon-mesh" || subtype === "polyface-mesh") return null;
  const run = heavyPolylineRuns(entity)[0];
  if (!run) return null;
  return heavyPolylineBand(
    entity,
    run,
    liveRunIndices(entity, run),
    compose(outer, ocsMatrix(entity)),
  );
}

/**
 * Top-level heavy `POLYLINE` → CadPrimitive[] (RAW world mm). A width-carrying
 * 2D / 3D run plots as its FILLED band; otherwise a mesh subtype yields one
 * stroked path per wire run and 2D / 3D yield a single stroked vertex run.
 */
export function heavyPolylineToPrimitives(
  entity: PolylineEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const band = heavyPolylineBandPath(entity);
  if (band) return bandPrimitives(entity, ctx, band);
  return strokedPathPrimitives(entity, ctx, heavyPolylinePaths(entity));
}
